import { useEffect, useState } from "react";
import { Job } from "./types";
import { useLogin } from "../../context/Login/LoginContext";
import { useSnackbar } from "notistack";
import {
    Typography,
    Box,
    CircularProgress,
    Paper,
    Stack
} from "@mui/material";
import { fetchJobs } from "./services/jobService";
import { SearchAndFilterSection } from "./components/SearchAndFilterSection";
import { JobCountAndSortSection } from "./components/JobCountAndSortSection";
import { JobCard } from "./components/JobCard";
import JobDetails from "./components/JobDetails";

const JobsList = () => {
    const [jobs, setJobs] = useState<Job[]>([]);
    const [loading, setLoading] = useState(true);
    const { enqueueSnackbar } = useSnackbar();
    const { openLoginDialog, isLoggedIn } = useLogin();

    const [selectedJob, setSelectedJob] = useState<Job | null>(null);

    const [location, setLocation] = useState("");
    const [sortBy, setSortBy] = useState("createdAt");
    const [sortOrder, setSortOrder] = useState("desc");
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        fetchJob();
    }, [searchQuery, location, sortBy, sortOrder]);

    const fetchJob = async () => {
        try {
            const jobsData = await fetchJobs(searchQuery, location, sortBy, sortOrder);
            setSelectedJob(jobsData.find(job => job._id === selectedJob?._id) ?? null);
            setJobs(jobsData);
        } catch (error) {
            enqueueSnackbar("Error when fetching jobs", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleSaveJob = () => {
        if (!isLoggedIn) {
            openLoginDialog();
            return;
        }
        enqueueSnackbar("Job saved successfully!", { variant: "success" });
    };

    const handleJobClick = (jobId: string) => {
        const job = jobs.find(job => job._id === jobId);
        if (job) {
            setSelectedJob(job);
        }
    };

    return (
        <Stack maxWidth={1} alignItems="center" overflow="auto">
            <Stack width="80%" m={2} >
                <SearchAndFilterSection searchQuery={searchQuery} setSearchQuery={setSearchQuery} location={location} setLocation={setLocation} />
                <JobCountAndSortSection jobs={jobs} jobsCount={jobs.length} sortOrder={sortOrder} setSortOrder={setSortOrder} sortBy={sortBy} setSortBy={setSortBy} />
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : jobs.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6">No jobs found</Typography>
                    </Paper>
                ) : (
                    <Stack direction="row" gap={2}>
                        <Stack flex={1} overflow="auto" gap={1} >
                            {jobs.map((job) => (
                                <JobCard
                                    key={job._id}
                                    job={job}
                                    selectedJobsId={selectedJob?._id ?? ""}
                                    onClick={handleJobClick}
                                    onSave={handleSaveJob}
                                />
                            ))}
                        </Stack>

                        <Box flex={2} position="sticky" top={10} sx={{ maxHeight: "90dvh", overflow: "auto" }}>
                            <JobDetails job={selectedJob} fetchJob={fetchJob} />
                        </Box>
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
}

export default JobsList;
